import { useCallback } from 'react';

import { useAnalyticsUtilities } from '../hooks/useAnalyticsUtilities';
import {
  type ShareUrlGeneratedComments,
  type ShareUrlGeneratedCustomFields,
  type ShareUrlGeneratedObjectType,
} from './useTrackGeneratedShareUrl';
import META from './useTrackShareUrlUpdated.meta';

export type ShareUrlUpdatedLinkAccess = 'enabled' | 'disabled';

export type ShareUrlUpdatedDownloads = 'none' | 'single_file' | 'all_assets';

export type ShareUrlUpdatedVersionControl = 'latest_only' | 'this_version' | 'all_versions';

export type ShareUrlUpdatedColorRole = 'header_background' | 'buttons_and_headlines' | 'page_background';

export type ShareUrlUpdatedPageWidth = 'narrow' | 'standard' | 'wide';

/** Named for the control the author sees; the setting persists as `thumbnailRadius`. */
export type ShareUrlUpdatedCornerRounding = 'standard' | 'subtle' | 'square';

/**
 * Only the two values the design tabs can author. The stored `colorMode` union also has a `toggle`
 * member (visitor's choice), which no authoring surface commits today.
 */
export type ShareUrlUpdatedPageTheme = 'light' | 'dark';

export type ShareUrlUpdatedSetting =
  | { updated_setting: 'link_access'; link_access: ShareUrlUpdatedLinkAccess }
  | { updated_setting: 'metadata'; metadata: boolean }
  | { updated_setting: 'custom_fields'; custom_fields: ShareUrlGeneratedCustomFields }
  | { updated_setting: 'comments'; comments: ShareUrlGeneratedComments }
  | { updated_setting: 'hide_search_input'; hide_search_input: boolean }
  | { updated_setting: 'hide_air_logo'; hide_air_logo: boolean }
  | { updated_setting: 'disable_view_settings'; disable_view_settings: boolean }
  | { updated_setting: 'disable_expanding_nested'; disable_expanding_nested: boolean }
  | { updated_setting: 'downloads'; downloads: ShareUrlUpdatedDownloads }
  | { updated_setting: 'version_control'; version_control: ShareUrlUpdatedVersionControl }
  | { updated_setting: 'custom_colors'; color_role: ShareUrlUpdatedColorRole; color_value: string | null }
  | { updated_setting: 'page_width'; page_width: ShareUrlUpdatedPageWidth }
  | { updated_setting: 'corner_rounding'; corner_rounding: ShareUrlUpdatedCornerRounding }
  | { updated_setting: 'page_theme'; page_theme: ShareUrlUpdatedPageTheme };

export type UseTrackShareUrlUpdatedParams = {
  share_id: string;
  object_type: ShareUrlGeneratedObjectType;
} & ShareUrlUpdatedSetting;

export const useTrackShareUrlUpdated = () => {
  const { track } = useAnalyticsUtilities();

  const trackShareUrlUpdated = useCallback(
    (params: UseTrackShareUrlUpdatedParams) => {
      track(META.name, params);
    },
    [track],
  );

  return { trackShareUrlUpdated };
};
